import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import arsClaimService, { type ArsClaim, type ArsClaimPayment, type PaymentReconciliation } from '../../../services/arsClaimService';
import ClaimStatusBadge from '../components/ClaimStatusBadge';
import GlossReviewPanel from '../components/GlossReviewPanel';
import ClaimPaymentsSection from '../components/ClaimPaymentsSection';
import PermissionGate from '../../../shared/components/PermissionGate';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import { formatCurrency } from '../../../lib/format';

const ClaimDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [claim, setClaim] = useState<ArsClaim | null>(null);
    const [payments, setPayments] = useState<ArsClaimPayment[]>([]);
    const [reconciliation, setReconciliation] = useState<PaymentReconciliation | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'items' | 'glosses' | 'payments'>('items');
    const [submitting, setSubmitting] = useState(false);

    const fetchClaim = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await arsClaimService.getById(parseInt(id));
            setClaim(res.data);
        } catch {
            setClaim(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchPayments = useCallback(async () => {
        if (!id) return;
        try {
            const res = await arsClaimService.getPayments(parseInt(id));
            if (res.success) {
                setPayments(res.data.payments);
                setReconciliation(res.data.reconciliation);
            }
        } catch {
            // ignore
        }
    }, [id]);

    useEffect(() => {
        fetchClaim();
    }, [fetchClaim]);

    useEffect(() => {
        if (claim) {
            fetchPayments();
        }
    }, [claim, fetchPayments]);

    const handleSubmit = async () => {
        if (!claim) return;
        setSubmitting(true);
        try {
            const res = await arsClaimService.submitClaim(claim.id);
            setClaim(res.data);
        } catch {
            alert('Error al enviar reclamación');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadSisalril = async () => {
        if (!claim) return;
        try {
            const res = await arsClaimService.generateSisalril(claim.id);
            const blob = new Blob([res.data.content], { type: 'text/plain;charset=Windows-1252' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = res.data.filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            alert('Error al descargar SISALRIL');
        }
    };

    const handleRefresh = () => {
        fetchClaim();
        fetchPayments();
    };

    const formatDate = (d: string | null) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatDateTime = (d: string | null) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    if (!claim) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-exclamation-circle fa-3x text-danger mb-3" />
                <h5>Reclamación no encontrada</h5>
                <Link to="/claims" className="btn btn-primary mt-2">Volver a Reclamaciones</Link>
            </div>
        );
    }

    const totalAuthorized = claim.items?.reduce((sum, item) => sum + (item.authorized_amount ?? 0), 0) ?? 0;

    return (
        <>
            <div className="page-header">
                <div className="row align-items-center">
                    <div className="col-sm-8">
                        <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Reclamaciones', to: '/claims' }, { label: claim.claim_number }]} />
                    </div>
                    <div className="col-sm-4 text-end">
                        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => navigate('/claims')}>
                            <i className="fa fa-arrow-left me-1" /> Volver
                        </button>
                        <button className="btn btn-outline-info btn-sm" onClick={handleRefresh}>
                            <i className="fa fa-refresh me-1" /> Actualizar
                        </button>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    {/* Claim Header */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h4 className="mb-1">
                                        <code>{claim.claim_number}</code>
                                        <span className="ms-2"><ClaimStatusBadge status={claim.status} /></span>
                                    </h4>
                                </div>
                                <div className="d-flex gap-1">
                                    <PermissionGate module="insurance" action="submit">
                                        {claim.status === 'draft' && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleSubmit}
                                                disabled={submitting}
                                            >
                                                <i className="fa fa-paper-plane me-1" />
                                                {submitting ? 'Enviando...' : 'Enviar Reclamación'}
                                            </button>
                                        )}
                                    </PermissionGate>
                                    <PermissionGate module="insurance" action="export">
                                        {(claim.status === 'submitted' || claim.status === 'accepted' || claim.status === 'partially_accepted') && (
                                            <button className="btn btn-secondary" onClick={handleDownloadSisalril}>
                                                <i className="fa fa-download me-1" /> Descargar SISALRIL
                                            </button>
                                        )}
                                    </PermissionGate>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Patient & Insurance Info */}
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="mb-2"><i className="fa fa-user me-1" /> Paciente</h6>
                                    <p className="mb-1">
                                        <strong>{claim.patient ? `${claim.patient.first_name} ${claim.patient.last_name}` : `#${claim.patient_id}`}</strong>
                                    </p>
                                    {claim.patient?.identification && (
                                        <small className="text-muted">Cédula: {claim.patient.identification}</small>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <h6 className="mb-2"><i className="fa fa-shield-halved me-1" /> Seguro</h6>
                                    <p className="mb-1"><strong>{claim.insurance_company?.name ?? '-'}</strong></p>
                                    <small className="text-muted">Plan: {claim.insurance_plan?.name ?? '-'}</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Amount Summary */}
                    <div className="row mb-3 g-2">
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-body text-center">
                                    <small className="text-muted d-block">Total Reclamado</small>
                                    <h4 className="mb-0">{formatCurrency(claim.claimed_amount)}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-body text-center">
                                    <small className="text-muted d-block">Autorizado</small>
                                    <h4 className="mb-0">
                                        {totalAuthorized > 0
                                            ? formatCurrency(totalAuthorized)
                                            : claim.authorized_amount
                                                ? formatCurrency(claim.authorized_amount)
                                                : '-'}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card">
                                <div className="card-body text-center">
                                    <small className="text-muted d-block">Fechas</small>
                                    <div className="small">
                                        <div>Envío: {formatDateTime(claim.submission_date)}</div>
                                        <div>Respuesta: {formatDateTime(claim.response_date)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-tabs mb-3" role="tablist">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'items' ? 'active' : ''}`}
                                role="tab"
                                aria-selected={activeTab === 'items'}
                                aria-controls="items"
                                onClick={() => setActiveTab('items')}
                            >
                                Items ({claim.items?.length ?? 0})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'glosses' ? 'active' : ''}`}
                                role="tab"
                                aria-selected={activeTab === 'glosses'}
                                aria-controls="glosses"
                                onClick={() => setActiveTab('glosses')}
                            >
                                Glosses ({claim.glosses?.length ?? 0})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
                                role="tab"
                                aria-selected={activeTab === 'payments'}
                                aria-controls="payments"
                                onClick={() => setActiveTab('payments')}
                            >
                                Pagos ({payments.length})
                            </button>
                        </li>
                    </ul>

                    {/* Tab Content */}
                    {activeTab === 'items' && (
                        <div className="card" id="items">
                            <div className="card-body">
                                <h6>Items de la Reclamación</h6>
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Descripción</th>
                                                <th>Cant.</th>
                                                <th>Precio Unit.</th>
                                                <th>Reclamado</th>
                                                <th>Autorizado</th>
                                                <th>Gloss</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claim.items?.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted">Sin items</td>
                                                </tr>
                                            )}
                                            {claim.items?.map(item => (
                                                <tr key={item.id}>
                                                    <td>{item.description}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{formatCurrency(item.unit_price)}</td>
                                                    <td>{formatCurrency(item.claimed_amount)}</td>
                                                    <td>
                                                        {item.authorized_amount !== null
                                                            ? formatCurrency(item.authorized_amount)
                                                            : <span className="text-muted">-</span>}
                                                    </td>
                                                    <td>
                                                        {item.gloss_code
                                                            ? <><code>{item.gloss_code}</code><br /><small>{item.gloss_description}</small></>
                                                            : <span className="text-muted">-</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {claim.notes && (
                                    <div className="mt-3">
                                        <strong>Notas:</strong>
                                        <p className="text-muted mb-0">{claim.notes}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'glosses' && (
                        <div id="glosses">
                            <GlossReviewPanel
                                claimId={claim.id}
                                glosses={claim.glosses ?? []}
                                onUpdate={fetchClaim}
                            />
                        </div>
                    )}

                    {activeTab === 'payments' && (
                        <div id="payments">
                            <ClaimPaymentsSection
                                claimId={claim.id}
                                payments={payments}
                                reconciliation={reconciliation}
                                onUpdate={fetchPayments}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ClaimDetailPage;
